import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { GoCheck } from 'react-icons/go';
import { MdUploadFile } from 'react-icons/md';
import api from '../../services/Api';
import Editor from '../../components/CKEditor';
import { useSelector } from 'react-redux';

const EditBlogModal = ({ blog, isOpen, onClose, onSave }) => {
  const userData = useSelector(state => state.auth.userData);
  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const response = await api.get('/industryListing/industries');
        if (response.data) {
          setIndustries(response.data.industries);
        }
      } catch (error) {
        console.error('Error fetching industries:', error);
      }
    };

    fetchIndustries();
  }, []);

  useEffect(() => {
    if (blog) {
      setPost({
        image: blog.imageUrl || null,
        title: blog.title || '',
        description: blog.description || '',
        content: blog.content || '',
        tag: '',
        featured: blog.isFeatured || false,
      });
      setSelectedCategory(blog.category || '');
      setSelectedTags(blog.tags || []);
    }
  }, [blog]);

  const [submitted, setSubmitted] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(blog?.category || '');
  const [selectedTags, setSelectedTags] = useState(blog?.tags || []);
  const [currentTag, setCurrentTag] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [post, setPost] = useState({
    image: blog?.imageUrl || null,
    title: blog?.title || '',
    description: blog?.description || '',
    content: blog?.content || '',
    tag: '',
    featured: blog?.isFeatured || false,
  });

  const [errors, setErrors] = useState({});
  const validateForm = () => {
    const errors = {};
    if (!post.title) errors.title = 'Title is required';
    if (!post.description) errors.description = 'Description is required';
    if (!post.content) errors.body = 'Body is required';
    if (selectedTags.length <= 0) errors.tags = 'At least one tag is required';
    if (!selectedCategory) errors.category = 'Category is required';

    return errors;
  };

  const handleCategoryChange = event => {
    const category = event.target.value;
    setSelectedCategory(category);

    const industry = industries.find(ind => ind.name === category);

    if (industry && industry.tags) {
      setAvailableTags(industry.tags);
    } else {
      console.log('No tags found');
      setAvailableTags([]);
    }
  };

  const removeTag = tagToRemove => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  };

  const handleInputChange = field => event => {
    const { value, checked } = event.target;

    if (field === 'featured') {
      setPost(prevPost => ({ ...prevPost, [field]: checked }));
    } else {
      setPost(prevPost => ({ ...prevPost, [field]: value }));
    }
  };

  const handleEditorContentChange = content => {
    setPost(prevPost => ({ ...prevPost, content: content }));
  };

  const handleImageUpload = event => {
    const file = event.target.files[0];
    if (file) {
      setPost({ ...post, image: file });
      setErrors({ ...errors, image: null });
    } else {
      setErrors({ ...errors, image: 'Please upload a valid image.' });
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitted(true);

    let imageUrl = post.image;

    if (post.image && post.image instanceof File) {
      try {
        const imageFormData = new FormData();
        imageFormData.append('file', post.image);

        const imageResponse = await api.post('/upload/file', imageFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (imageResponse.data && imageResponse.data.url) {
          imageUrl = imageResponse.data.url;
        } else {
          throw new Error('Image upload failed');
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Image upload failed. Please try again.');
        setSubmitted(false);
        return;
      }
    }

    let adminId = null;
    try {
      if (userData) {
        adminId = userData?.sub;
        console.log('adminId:', adminId);
      }
    } catch (error) {
      console.error('Error parsing userData:', error);
    }

    console.log('Extracted adminId:', adminId);

    if (!blog || !blog._id) {
      console.error('Invalid blog object:', blog);
      alert('Error: Unable to identify the blog to update');
      setSubmitted(false);
      return;
    }

    const blogData = {
      adminId: adminId,
      userId: adminId,
      blogId: blog._id,
      title: post.title,
      content: post.content,
      description: post.description,
      category: selectedCategory,
      tags: selectedTags,
      isFeatured: post.featured,
      imageUrl: imageUrl,
    };

    // Comprehensive logging
    console.group('Blog Update Data');
    Object.entries(blogData).forEach(([key, value]) => {
      console.log(
        `${key}:`,
        key === 'content' ? (value ? `${value.substring(0, 100)}...` : value) : value
      );
    });
    console.groupEnd();

    try {
      await onSave(blogData);
      onClose();
    } catch (error) {
      console.error('Full error updating blog post:', error);
      console.error('Error response:', error.response);
      alert(`Error updating blog post: ${error.message}`);
    } finally {
      setSubmitted(false);
    }
  };

  // Early return if modal should be hidden
  if (!isOpen || !blog) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-black bg-opacity-50">
      <div className="relative bg-white rounded-xl ml-64 w-[70%] max-w-6xl max-h-[70vh] overflow-auto font-nohemi">
        <button
          onClick={onClose}
          className="absolute z-10 text-gray-500 top-4 right-4 hover:text-gray-700">
          <span className="text-2xl">&times;</span>
        </button>

        <div className="p-6">
          <h2 className="mb-4 text-xl font-normal">Edit Blog Post</h2>

          <form onSubmit={handleSubmit}>
            <div className="rounded">
              <div className="bg-white rounded-xl">
                <div className="flex flex-col gap-6 lg:flex-row">
                  {/* Left side - Editor */}
                  <div className="flex-1 w-2/3">
                    <div className="border border-gray-300 rounded-xl">
                      <div className="p-5 pb-4">
                        {/* Blog Image */}
                        <div
                          className={`lg:w-7/12 flex items-center justify-center border-2 border-dashed ${
                            errors?.image ? 'border-red-500' : 'border-gray-300'
                          } rounded-2xl mt-1 mx-auto`}>
                          <div className="flex flex-col items-center gap-2 px-5 py-8">
                            <div className="flex items-center justify-center w-10 h-10 text-center bg-gray-100 rounded-full">
                              {post.image ? (
                                <GoCheck className="text-xl text-green-500" />
                              ) : (
                                <MdUploadFile className="text-xl" />
                              )}
                            </div>
                            {post.image && (
                              <p className="max-w-full px-2 text-xs font-light text-green-600 break-all">
                                {post.image?.name || 'Current image'}
                              </p>
                            )}
                            <input
                              type="file"
                              id="banner_input"
                              name="image"
                              accept="image/jpeg, image/png"
                              style={{ display: 'none' }}
                              onChange={handleImageUpload}
                            />
                            <div className="text-xs text-center lg:text-xs">
                              <p className="">
                                <label htmlFor="banner_input" className="font-light cursor-pointer">
                                  Click Here
                                </label>{' '}
                                &nbsp;
                                <span>to upload your file or drag</span>
                              </p>
                              <p className="text-xs text-gray-500 lg:text-xs">
                                Supported Formats: JPG, PNG, JPEG (10mb each)
                              </p>
                            </div>
                          </div>
                        </div>
                        {errors?.image && (
                          <span className="block mt-2 text-xs text-red-500 lg:text-center">
                            {errors?.image}
                          </span>
                        )}

                        {/* Title */}
                        <input
                          type="text"
                          id="title"
                          name="title"
                          value={post.title}
                          onChange={handleInputChange('title')}
                          className="w-full px-4 py-3 mt-5 mb-1 text-lg border-b border-gray-300 rounded focus:outline-none"
                          placeholder="Title"
                        />
                        {errors?.title && (
                          <span className="text-xs text-red-500">{errors?.title}</span>
                        )}

                        {/* Description */}
                        <input
                          type="text"
                          id="description"
                          name="description"
                          value={post.description}
                          onChange={handleInputChange('description')}
                          className="w-full mb-1 px-4 py-3 bg-[#FBFBFB] text-xs rounded border-none focus:outline-none"
                          placeholder="Write here a short description"
                        />
                        {errors?.description && (
                          <span className="text-xs text-red-500">{errors?.description}</span>
                        )}

                        {/* Editor */}
                        <Editor onChange={handleEditorContentChange} value={post.content} />
                        {errors?.body && (
                          <span className="text-xs text-red-500">{errors?.body}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right side - Publishing Options */}
                  <div className="lg:w-[300px] flex-shrink-0">
                    <div className="p-4 bg-white border border-gray-300 rounded-xl">
                      <h3 className="mb-4">PUBLISH</h3>

                      <div className="space-y-4">
                        <div>
                          <label htmlFor="category" className="block mb-2 text-xs">
                            Select blog category
                          </label>
                          <select
                            className="w-full p-2.5 my-1 border border-gray-300 rounded-lg focus:outline-none"
                            value={selectedCategory}
                            onChange={handleCategoryChange}>
                            <option value="">Select blog Category</option>
                            {industries &&
                              industries?.map(item => (
                                <option key={item._id} value={item.name}>
                                  {item.name}
                                </option>
                              ))}
                          </select>
                          {errors?.category && (
                            <span className="text-xs text-red-500">{errors?.category}</span>
                          )}
                        </div>

                        <div className="px-5 py-3 border border-gray-300 rounded-xl">
                          {selectedTags.length >= 1 && (
                            <>
                              <label htmlFor="tags" className="block mb-2 text-xs">
                                Selected Tags
                              </label>
                              <div className="flex flex-wrap gap-2 mb-2">
                                {selectedTags.map((tag, index) => (
                                  <span
                                    key={tag.id || index}
                                    className="px-2 py-1 bg-[#ECECEC] rounded text-xs flex items-center gap-2">
                                    {tag}{' '}
                                    <button
                                      type="button"
                                      onClick={() => removeTag(tag)}
                                      className="text-lg text-gray-500">
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </>
                          )}

                          <label htmlFor="addTags" className="block mt-3 mb-2 text-xs">
                            Add a tag
                          </label>
                          <div className="flex items-center gap-3 text-xs">
                            <select
                              className="w-full p-2.5 my-1 border border-gray-300 rounded"
                              value={currentTag}
                              onChange={e => {
                                setCurrentTag(e.target.value);
                              }}>
                              <option value="">Select a tag</option>
                              {availableTags.length > 0 ? (
                                availableTags.map((tag, index) => (
                                  <option key={tag.id || index} value={tag}>
                                    {tag}
                                  </option>
                                ))
                              ) : (
                                <option disabled>No tags available</option>
                              )}
                            </select>
                            <button
                              type="button"
                              disabled={!currentTag}
                              onClick={() => {
                                if (currentTag && !selectedTags.includes(currentTag)) {
                                  setSelectedTags(prevTags => [...prevTags, currentTag]);
                                  setCurrentTag('');
                                }
                              }}
                              className="bg-[#ECECEC] text-[#797979] text-xs px-4 py-2 rounded cursor-pointer">
                              Add
                            </button>
                          </div>
                          {errors?.tags && (
                            <span className="text-xs text-red-500">{errors?.tags}</span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="featured"
                            checked={post.featured}
                            onChange={handleInputChange('featured')}
                            className="w-4 h-4 accent-black"
                          />
                          <label htmlFor="featured">Mark post as featured</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-center mt-6 lg:justify-start">
              <button
                type="button"
                onClick={onClose}
                className="px-8 py-3 mr-4 text-black border border-black rounded">
                Cancel
              </button>
              <button type="submit" className="px-8 py-3 text-white bg-black rounded w-44">
                {submitted ? <div className="text-center">Saving...</div> : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
EditBlogModal.propTypes = {
  blog: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    content: PropTypes.string,
    imageUrl: PropTypes.string,
    category: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    isFeatured: PropTypes.bool,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default EditBlogModal;
